import { useState, useEffect } from "react";

import { useLayout } from "../../../Utils/Context/LayoutContext";

import "./FruitOfTheSpirit.scss";

// ─── DATA ─────────────────────────────────────────────────────────────────────

const fruits = [
  {
    name: "Love",
    icon: "❤️",
    accent: "#8b1a1a",
    struggling: {
      desc: "Struggling to love others or feel loved",
      verse:
        '"Love is patient, love is kind... it bears all things, believes all things, hopes all things, endures all things." — 1 Corinthians 13:4–7',
      prayerStarter:
        "Lord, my heart is tired and I am running out of love to give. Teach me to love not from my own strength, but from Yours. Fill me with Your love for _____, because on my own I cannot. Help me see them the way You see them — as someone worthy of the same grace You have shown me. Remind me today that love is not a feeling I wait for; it is a decision I make in obedience to You...",
      people: [
        {
          icon: "🕊️",
          name: "Hosea",
          role: "Prophet of Israel",
          context:
            "Hosea was called by God to love a wife — Gomer — who repeatedly abandoned him for other men. He faced deep betrayal, public shame, and the exhaustion of a love that was never returned.",
          quote:
            "The LORD said to him: 'Go again, love a woman who is loved by a lover and is committing adultery, just like the love of the LORD for the children of Israel.'",
          ref: "Hosea 3:1",
          outcome:
            "Hosea obeyed. He went and bought his wife back from slavery — not because she deserved it, but because God commanded it. In doing so, he became a living picture of God's relentless covenant love. When love feels impossible, remember: love is not earned, it is chosen. Ask God to make it a decision before it becomes a feeling.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🤍",
          name: "The Prodigal Son's Father",
          role: "Parable told by Jesus",
          context:
            "This father endured a son who wished him dead (in asking for his inheritance early), squandered everything, and brought deep shame to the family. Yet he waited, watched, and wept.",
          quote:
            "But when he was still a great way off, his father saw him and had compassion, and ran and fell on his neck and kissed him.",
          ref: "Luke 15:20",
          outcome:
            "The father ran. He did not make his son grovel or earn his way back — he interrupted the rehearsed apology with robe, ring, and feast. When you struggle to love someone who has hurt you, ask God to give you His eyes — to see them as someone running home.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Grateful for the love God has placed in your heart",
      verse: '"We love because He first loved us." — 1 John 4:19',
      prayerStarter:
        "Father, thank You for the capacity to love that You have placed in me — I know it comes entirely from You. Today I want to honor You by loving _____ well. Show me how to be a vessel of Your love, and remind me that every act of genuine love I give is a reflection of who You are. Don't let this love stay inside me — let it overflow into every room I enter today...",
      people: [
        {
          icon: "🌊",
          name: "Jonathan",
          role: "Son of King Saul",
          context:
            "Jonathan loved David with a covenant love so deep that he gave up his own claim to the throne of Israel. He protected David from his own father's murderous rage and chose friendship over crown and comfort.",
          quote:
            "Then Jonathan and David made a covenant, because he loved him as his own soul. And Jonathan took off the robe that was on him and gave it to David, with his armor, even to his sword and his bow and his belt.",
          ref: "1 Samuel 18:3–4",
          outcome:
            "Jonathan's love was sacrificial, costly, and joyful. He was not diminished by giving — he was made greater. When love flows freely in your life, recognize it as a gift to steward. Give it away lavishly. Let it cost you something. That is when it most resembles God.",
          outcomeLabel: "How He Expressed It",
        },
        {
          icon: "📜",
          name: "Paul",
          role: "Apostle to the Gentiles",
          context:
            "Paul wrote 1 Corinthians 13 — the greatest description of love in all of Scripture — from decades of costly ministry. He had loved the church, his enemies, and his brothers through beatings, imprisonments, and betrayals.",
          quote:
            "And now abide faith, hope, love, these three; but the greatest of these is love.",
          ref: "1 Corinthians 13:13",
          outcome:
            "Paul gave thanks by pouring love into words that would outlast his own life. When love fills your heart, express it — write it, speak it, embody it. Your gratitude for love received is best expressed through love freely given away.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Joy",
    icon: "☀️",
    accent: "#b8860b",
    struggling: {
      desc: "Feeling joyless, depressed, or empty",
      verse: '"The joy of the LORD is your strength." — Nehemiah 8:10',
      prayerStarter:
        "God, I confess I cannot find joy on my own right now. My heart feels heavy and I am going through the motions. I don't ask You to change my circumstances — I ask You to change me. Be my joy when joy will not come on its own. Remind me of what is true when all I can feel is _____, and help me choose praise before I feel it...",
      people: [
        {
          icon: "🎵",
          name: "David",
          role: "King of Israel, Psalmist",
          context:
            "David wrote Psalm 22 from complete desolation — feeling utterly forsaken by God, surrounded by enemies, his body wasting away. 'My God, My God, why have You forsaken Me?' — the same words Jesus cried from the cross.",
          quote:
            "Why are you cast down, O my soul? And why are you disquieted within me? Hope in God, for I shall yet praise Him for the help of His countenance.",
          ref: "Psalm 42:5",
          outcome:
            "David preached to his own soul. He did not wait to feel joy before he praised — he chose praise as the path back to joy. The Psalms show us that lament and praise are not opposites; they are a conversation with God that always ends in 'yet I will trust Him.' Speak to your soul before your soul speaks to you.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "⛓️",
          name: "Paul & Silas",
          role: "Apostle and Missionary",
          context:
            "After being beaten with rods and thrown into the innermost part of a prison with their feet in stocks, Paul and Silas had every reason to despair. Their bodies were broken and their mission seemed crushed.",
          quote:
            "But at midnight Paul and Silas were praying and singing hymns to God, and the prisoners were listening to them.",
          ref: "Acts 16:25",
          outcome:
            "They worshipped at midnight — the darkest hour. They sang not because circumstances were good but because God was good. The earthquake that followed broke every chain. When joy is gone, sing anyway. Worship in the dark. The chains that bind you are not stronger than the God who hears.",
          outcomeLabel: "How They Overcame",
        },
      ],
    },
    thriving: {
      desc: "Overflowing with joy and wanting to honor God with it",
      verse:
        '"Rejoice in the Lord always. Again I will say, rejoice!" — Philippians 4:4',
      prayerStarter:
        "Lord, my heart is full and I know this joy comes from You. Thank You for this season of gladness. I want to use this joy to point others to You — don't let me hoard it. Help me give it away freely today. Let my gladness be a witness that You are good, especially to those around me who are still in their midnight season...",
      people: [
        {
          icon: "🎶",
          name: "Mary",
          role: "Mother of Jesus",
          context:
            "When Mary visited her cousin Elizabeth after learning she would bear the Son of God, she burst into the Magnificat — one of the most profound songs of praise in Scripture. She was unwed, facing social ruin, and yet overflowing with joy.",
          quote:
            "My soul magnifies the Lord, and my spirit has rejoiced in God my Savior.",
          ref: "Luke 1:46–47",
          outcome:
            "Mary's joy was not circumstantial — it was prophetic. She praised God for what He would do before she could see it. When joy fills you, let it be an act of worship. Magnify God with it. Let others hear it and know it came from Him.",
          outcomeLabel: "How She Expressed It",
        },
        {
          icon: "🌅",
          name: "The Disciples After the Resurrection",
          role: "Followers of Jesus",
          context:
            "After Jesus appeared to them, the disciples went from hiding in locked rooms to being continually in the temple praising God openly. Fear became fearless worship.",
          quote:
            "And they worshiped Him, and returned to Jerusalem with great joy, and were continually in the temple praising and blessing God.",
          ref: "Luke 24:52–53",
          outcome:
            "Their joy made them bold. When joy fills your heart, let it make you braver. Use it as fuel for praise and generosity. Joy hoarded becomes stagnant — joy given away multiplies.",
          outcomeLabel: "How They Expressed It",
        },
      ],
    },
  },
  {
    name: "Peace",
    icon: "🕊️",
    accent: "#1a3a5c",
    struggling: {
      desc: "Overwhelmed by anxiety or fear",
      verse:
        '"And the peace of God, which surpasses all understanding, will guard your hearts and minds through Christ Jesus." — Philippians 4:7',
      prayerStarter:
        "Jesus, I am anxious and I am afraid. My mind keeps returning to _____ and I cannot stop the worry. I confess I am trying to carry something You never asked me to carry alone. I lay it at Your feet right now. Guard my mind. Be the peace that I cannot manufacture for myself. Help me trust that You are in this boat with me...",
      people: [
        {
          icon: "⛵",
          name: "The Disciples in the Storm",
          role: "Followers of Jesus",
          context:
            "Caught in a violent storm on the Sea of Galilee, the disciples — experienced fishermen — believed they were going to die. Jesus was asleep in the boat. Their fear became panic, then accusation: 'Do You not care?'",
          quote:
            "'Peace, be still!' And the wind ceased and there was a great calm. He said to them, 'Why are you so fearful? How is it that you have no faith?'",
          ref: "Mark 4:39–40",
          outcome:
            "Jesus was in the boat with them all along — and He is in your storm too. Peace was always available. Turn your panic into prayer: 'Do You not care?' is still a prayer. He answers it the same way — with His presence and power.",
          outcomeLabel: "How They Overcame",
        },
        {
          icon: "🙏",
          name: "Elijah",
          role: "Prophet of Israel",
          context:
            "After his greatest victory on Mount Carmel, Elijah ran into the wilderness, sat under a broom tree, and begged God to let him die. He was burned out, terrified of Jezebel, and completely alone. Anxiety followed triumph.",
          quote:
            "'It is enough! LORD, take my life, for I am no better than my fathers!'",
          ref: "1 Kings 19:4",
          outcome:
            "God did not rebuke Elijah. He let him sleep, then an angel touched him and said, 'Arise and eat, because the journey is too great for you.' God met his physical needs first — food, water, rest — then spoke in a still small voice. When anxiety overwhelms you, tend to your body and listen for the whisper.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Resting in God's peace and wanting to share it",
      verse:
        '"You will keep him in perfect peace, whose mind is stayed on You, because he trusts in You." — Isaiah 26:3',
      prayerStarter:
        "Lord, thank You for this stillness. I know this peace is not something I earned — it is Your gift. Help me be a carrier of Your peace into every room I enter today. Let me be someone who brings calm instead of chaos, and remind me that this peace is not mine to cling to but Yours to flow through me freely...",
      people: [
        {
          icon: "🌙",
          name: "Jesus in Gethsemane",
          role: "Son of God",
          context:
            "On the night before His crucifixion, Jesus was in anguish — sweating drops of blood. Yet He prayed through it and rose with peace and resolve, going willingly to face what lay ahead.",
          quote:
            "'Father, if it is Your will, take this cup away from Me; nevertheless not My will, but Yours, be done.'",
          ref: "Luke 22:42",
          outcome:
            "Jesus modeled peace that passes through suffering, not around it. He found peace not by changing circumstances but by surrendering His will. When you are at peace, let it flow from surrender. Thank God that your will and His are aligned — and stay there.",
          outcomeLabel: "How He Expressed It",
        },
        {
          icon: "📖",
          name: "Paul in Prison",
          role: "Apostle, writing from chains",
          context:
            "Paul wrote Philippians — the most joy-filled letter in the New Testament — from a Roman prison cell, not knowing if he would live or die. He had learned the secret of contentment in all circumstances.",
          quote:
            "I have learned, in whatever state I am, to be content... I can do all things through Christ who strengthens me.",
          ref: "Philippians 4:11,13",
          outcome:
            "Paul's peace was not the absence of hardship — it was the presence of Christ. When peace fills your heart, write it down, speak it over others, and remember it for the next storm. Peace remembered is faith built.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Patience",
    icon: "⏳",
    accent: "#4a5e2a",
    struggling: {
      desc: "Struggling with waiting or trusting God's timing",
      verse:
        '"Wait on the LORD; be of good courage, and He shall strengthen your heart; wait, I say, on the LORD!" — Psalm 27:14',
      prayerStarter:
        "Father, I am tired of waiting. I know You are good, but I cannot see what You are doing and the silence is hard. I confess I have tried to take _____ into my own hands. I lay it back down before You today. Give me the courage to wait, and show me what You want to build in me during this season of in-between...",
      people: [
        {
          icon: "⭐",
          name: "Abraham",
          role: "Father of Many Nations",
          context:
            "God promised Abraham a son when he was 75 years old. He waited 25 years while his body aged and Sarah's womb remained empty. He doubted enough to try to fulfill the promise himself through Hagar — and suffered the consequences.",
          quote:
            "He did not waver at the promise of God through unbelief, but was strengthened in faith, giving glory to God, and being fully convinced that what He had promised He was also able to perform.",
          ref: "Romans 4:20–21",
          outcome:
            "Abraham's faith was not perfect — but it was persistent. He stumbled, doubted, and tried to help God — yet he kept returning. Isaac came when Abraham was 100. God's promises have gestation periods we cannot control. The waiting is not wasted time; it is the very place where faith is formed.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "💀",
          name: "Job",
          role: "Man of Uz",
          context:
            "Job lost his children, his wealth, his health, and the comfort of his friends — all in rapid succession. He sat in ashes for days, demanded an audience with God, and accused God of being unjust.",
          quote:
            "'Though He slay me, yet will I trust Him... He also shall be my salvation, for a hypocrite could not come before Him.'",
          ref: "Job 13:15–16",
          outcome:
            "Job never got an explanation for his suffering. What he got was God Himself — overwhelming him with presence and majesty rather than answers. Honest, anguished lament before God is not faithlessness — it is faith with nothing left to hide.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Experiencing God's faithfulness after a long wait",
      verse:
        '"Those who wait on the LORD shall renew their strength; they shall mount up with wings like eagles." — Isaiah 40:31',
      prayerStarter:
        "Lord, You came through. After all the waiting, I can see Your hand now, and I am humbled. Thank You for not answering on my timeline — Your timing was perfect. I want to use this testimony to encourage someone who is still waiting. Help me never forget what it felt like to be in the in-between, so I can sit with others who still are...",
      people: [
        {
          icon: "👶",
          name: "Hannah",
          role: "Mother of Samuel",
          context:
            "Hannah wept and fasted and prayed for years for a son while being mocked by Peninnah. She poured out her soul before God in the temple and made a covenant. When Samuel was born, she gave him back to God.",
          quote:
            "My heart rejoices in the LORD; my horn is exalted in the LORD. I smile at my enemies, because I rejoice in Your salvation.",
          ref: "1 Samuel 2:1",
          outcome:
            "Hannah's song of thanksgiving became the template for Mary's Magnificat centuries later. Her gratitude was not private — it was prophetic praise that echoed across generations. When God answers your long prayer, let your thanksgiving overflow into someone else's faith.",
          outcomeLabel: "How She Expressed It",
        },
        {
          icon: "🌈",
          name: "Noah",
          role: "Builder of the Ark",
          context:
            "After 120 years of building and one year on the water, Noah stepped onto dry land. The first thing he did was build an altar and offer burnt offerings to God — an act of extravagant gratitude before he did anything else.",
          quote:
            "Then Noah built an altar to the LORD, and took of every clean animal and every clean bird, and offered burnt offerings on the altar.",
          ref: "Genesis 8:20",
          outcome:
            "Noah's first response to answered patience was worship — not celebration or relief, but an altar. When the waiting is finally over, build an altar. Name what God did. Tell the story. Gratitude expressed becomes a monument for the next generation.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Kindness",
    icon: "🤲",
    accent: "#6b4a8a",
    struggling: {
      desc: "Finding it hard to show kindness to difficult people",
      verse:
        '"Be kind to one another, tenderhearted, forgiving one another, even as God in Christ forgave you." — Ephesians 4:32',
      prayerStarter:
        "Lord, I am finding it very difficult to be kind to _____. If I am honest, I don't want to be. Help me see them through Your eyes and not my own hurt. Soften my heart where it has become hard. Remind me how much grace You have shown me — and let that overflow into how I treat them today. I cannot do this without You...",
      people: [
        {
          icon: "👑",
          name: "David & Mephibosheth",
          role: "King and the Crippled Son",
          context:
            "Mephibosheth was the crippled son of Jonathan and grandson of Saul — David's old enemy. He was hiding, expecting death. David had every political reason to ignore or eliminate him.",
          quote:
            "'Do not fear, for I will surely show you kindness for Jonathan your father's sake, and will restore to you all the land of Saul your grandfather; and you shall eat bread at my table continually.'",
          ref: "2 Samuel 9:7",
          outcome:
            "David sought out someone he had no obligation to bless and treated him as one of his own sons. This is hesed — covenant kindness. It is not kindness toward the deserving; it is kindness extended for the sake of love. Ask: whose Jonathan do you have a covenant with? Whose Mephibosheth are you ignoring?",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🛤️",
          name: "The Good Samaritan",
          role: "Parable told by Jesus",
          context:
            "The Samaritan stopped for a man from a culture that despised his people. He crossed every social, racial, and religious boundary. The religious leaders had already passed by.",
          quote:
            "'Which of these three do you think was a neighbor to the man who fell into the hands of robbers?' 'The one who had mercy on him.' Jesus told him, 'Go and do likewise.'",
          ref: "Luke 10:36–37",
          outcome:
            "Jesus redefined 'neighbor' so broadly that no one is excluded. The Samaritan did not stop because it was convenient — he stopped because need was in front of him. Kindness is not a feeling; it is seeing a need and crossing whatever road lies between you and it.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Experiencing the joy of showing God's kindness to others",
      verse:
        '"The LORD your God... is gracious and merciful, slow to anger and of great kindness." — Joel 2:13',
      prayerStarter:
        "Father, thank You for the opportunities You have given me to show kindness. I know every moment of genuine kindness is You working through me. Open my eyes to see the people around me today who need a word, a gesture, or a moment of simply being seen. Let my kindness be so unexplainable that it points people back to You...",
      people: [
        {
          icon: "🌿",
          name: "Boaz",
          role: "Kinsman-Redeemer of Ruth",
          context:
            "Boaz noticed Ruth gleaning at the edges of his field — a foreign widow with nothing. He went far beyond the law, commanding his workers to leave extra grain for her and protect her, and inviting her to eat at his table.",
          quote:
            "Then he said to her, 'Reach out your hand and take some food, and dip your piece of bread in the vinegar'... And Boaz commanded his young men, saying, 'Leave some for her, and do not rebuke her.'",
          ref: "Ruth 2:14,16",
          outcome:
            "Boaz's kindness was intentional, practical, and extravagant beyond what was required. He saw someone invisible and made her seen. When kindness flows from you, go beyond what is expected. Let it cost you something. That is when it most resembles grace.",
          outcomeLabel: "How He Expressed It",
        },
        {
          icon: "🤗",
          name: "Barnabas",
          role: "Encourager in the Early Church",
          context:
            "When Saul of Tarsus — the feared persecutor of Christians — claimed to have met Jesus, no one believed him. Every door was closed. Barnabas stood up for him, brought him to the apostles, and vouched for him personally.",
          quote:
            "But Barnabas took him and brought him to the apostles. And he declared to them how he had seen the Lord on the road, and that He had spoken to him.",
          ref: "Acts 9:27",
          outcome:
            "Barnabas's kindness launched the greatest missionary career in church history. One act of believing the best about someone changed the world. When kindness fills you, use it on the people others have written off. You don't know who you are releasing.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Goodness",
    icon: "✨",
    accent: "#8b1a45",
    struggling: {
      desc: "Struggling with temptation or moral failure",
      verse:
        '"O wretched man that I am! Who will deliver me from this body of death? I thank God — through Jesus Christ our Lord!" — Romans 7:24–25',
      prayerStarter:
        "God, I have failed. I am ashamed and I want to hide, but I know that running from You only makes it worse. I confess _____ to You right now — all of it. I don't deserve Your mercy, but I am asking for it anyway. Create in me a clean heart. Don't let my failure be the last word. Restore me and use even this for Your glory...",
      people: [
        {
          icon: "💔",
          name: "David after Bathsheba",
          role: "King of Israel",
          context:
            "David committed adultery with Bathsheba and arranged the murder of her husband Uriah to cover it up. He lived with that hidden sin for months, his bones wasting away, until Nathan the prophet confronted him.",
          quote:
            "'Have mercy on me, O God, according to Your lovingkindness... Create in me a clean heart, O God, and renew a steadfast spirit within me.'",
          ref: "Psalm 51:1,10",
          outcome:
            "Psalm 51 is David's confession — raw, unguarded, brutally honest. He did not excuse his sin or minimize it. He called it what it was and threw himself on God's mercy. God called David 'a man after My own heart' not because he never sinned, but because he always came back. Don't run from God — run to Him.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🐓",
          name: "Peter",
          role: "Apostle, Rock of the Church",
          context:
            "Peter swore he would die before he denied Jesus. Hours later, by a courtyard fire, he denied knowing Jesus three times. The rooster crowed, Jesus turned and looked at him, and Peter went out and wept bitterly.",
          quote:
            "Peter said, 'Lord, You know all things; You know that I love You.' Jesus said to him, 'Feed My sheep.'",
          ref: "John 21:17",
          outcome:
            "Jesus did not disqualify Peter. He restored him — three affirmations for three denials, beside a charcoal fire just like the one where Peter fell. He gave Peter the very ministry he had failed at. Your worst failure does not write the last chapter. Jesus meets you at the place of your greatest shame and reassigns you.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Walking in integrity and experiencing God's blessing",
      verse:
        '"Blessed are the pure in heart, for they shall see God." — Matthew 5:8',
      prayerStarter:
        "Father, thank You for this season of walking closely with You. I know this integrity is not my own — it is the fruit of Your Spirit in me. Guard my heart and keep me humble. Remind me that goodness maintained in ordinary moments is a form of worship. Don't let me take this season for granted or let down my guard when I feel strong...",
      people: [
        {
          icon: "🌟",
          name: "Joseph",
          role: "Son of Jacob, Ruler of Egypt",
          context:
            "Joseph was sold into slavery by his brothers, falsely accused by Potiphar's wife, and forgotten in prison — yet he maintained integrity at every turn. When he finally had power over his brothers, he wept and forgave.",
          quote:
            "'You intended to harm me, but God intended it for good to accomplish what is now being done, the saving of many lives.'",
          ref: "Genesis 50:20",
          outcome:
            "Joseph's goodness was not naive — it was costly and tested. He gave thanks not because his path had been easy but because he could see God's hand in all of it. When you are walking in goodness, look back. Name what God has done. Your testimony is someone else's lifeline.",
          outcomeLabel: "How He Expressed It",
        },
        {
          icon: "📜",
          name: "Daniel",
          role: "Prophet in Babylon",
          context:
            "Daniel resolved from his teenage years not to defile himself. He maintained that integrity through decades of serving pagan kings — and was found without fault even by those who searched specifically for something to accuse him of.",
          quote:
            "So Daniel prospered in the reign of Darius and in the reign of Cyrus the Persian.",
          ref: "Daniel 6:28",
          outcome:
            "Daniel's goodness was not a performance — it was a settled conviction formed early and held consistently. He prayed three times a day in thanksgiving, making gratitude a discipline. When you are walking in goodness, make thanksgiving a daily habit, not an occasional event.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Faithfulness",
    icon: "🔑",
    accent: "#1a4a3a",
    struggling: {
      desc: "Feeling like giving up or losing faith",
      verse:
        '"Be faithful until death, and I will give you the crown of life." — Revelation 2:10',
      prayerStarter:
        "Lord, I am tired. I have been faithful for so long and I am not sure I can keep going. I don't see the fruit. I am questioning whether any of this matters. Help me not to walk away. Give me just enough grace for today — I am not asking for the whole road, just the next step. Remind me why I started following You...",
      people: [
        {
          icon: "🦁",
          name: "Daniel",
          role: "Prophet in Babylon",
          context:
            "Daniel was taken captive as a teenager and forced to live in a pagan empire for decades. He was thrown into the lions' den specifically because of his faithfulness in prayer. He knew the law had been signed; he prayed anyway.",
          quote:
            "He knelt down on his knees three times that day, and prayed and gave thanks before his God, as was his custom since early days.",
          ref: "Daniel 6:10",
          outcome:
            "Daniel's faithfulness was not heroic in one dramatic moment — it was decades of quiet daily habit. He knelt three times a day long before the lions ever appeared. By the time the crisis came, faithfulness was simply who he was. Build the habits of faithfulness in the ordinary days, and they will carry you through the extraordinary ones.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🌾",
          name: "Ruth",
          role: "Moabite Woman, Ancestor of Jesus",
          context:
            "Ruth's husband died, leaving her a foreign widow with nothing. Her mother-in-law Naomi released her. She had nothing to gain by staying — no promise of provision, no guarantee of belonging.",
          quote:
            "'Where you go, I will go; and where you lodge, I will lodge; your people shall be my people, and your God, my God.'",
          ref: "Ruth 1:16",
          outcome:
            "Ruth chose faithfulness to a grieving widow with nothing to offer. She gleaned fields, humbled herself, and trusted God in the mundane work of survival. Her faithfulness was rewarded beyond anything she could have imagined — she entered the lineage of Jesus Christ. Faithfulness in the hidden places is never invisible to God.",
          outcomeLabel: "How She Overcame",
        },
      ],
    },
    thriving: {
      desc: "Grateful for a season of deep faithfulness and fruitfulness",
      verse:
        '"Well done, good and faithful servant... Enter into the joy of your lord." — Matthew 25:23',
      prayerStarter:
        "Father, I am humbled by this season. I know the faithfulness I have been able to walk in is not my own strength — it is entirely Your grace. Thank You for sustaining me when I wanted to quit. Use this season to encourage someone else who is struggling to hold on. Let my story be a testimony that You are worth trusting all the way through...",
      people: [
        {
          icon: "🌈",
          name: "Caleb",
          role: "Faithful Spy, Conqueror of Hebron",
          context:
            "At 85 years old, Caleb asked for the hardest territory in Canaan — the mountain of the giants — as his inheritance. He had held onto faith in God's promise for 45 years while everyone around him doubted.",
          quote:
            "'I wholly followed the LORD my God... now give me this mountain of which the LORD spoke in that day.'",
          ref: "Joshua 14:8,12",
          outcome:
            "Caleb's decades of faithfulness produced boldness, not bitterness. He was not cynical or coasting — he was eager to conquer at 85. When faithfulness has carried you far, don't coast. Let your endurance be an invitation to others: 'Follow me. God is worth it.'",
          outcomeLabel: "How He Expressed It",
        },
        {
          icon: "🕊️",
          name: "Anna",
          role: "Prophetess in the Temple",
          context:
            "Anna had been a widow for 84 years, serving in the temple night and day with fasting and prayer. When Mary and Joseph brought the infant Jesus to be dedicated, she recognized Him instantly and gave thanks.",
          quote:
            "And coming in that instant she gave thanks to the Lord, and spoke of Him to all those who looked for redemption in Jerusalem.",
          ref: "Luke 2:38",
          outcome:
            "Anna's decades of faithful, quiet, unseen prayer positioned her to be one of the first to proclaim the Messiah. She immediately turned her thanksgiving into proclamation — she told everyone. Let your gratitude for faithfulness sustained become a testimony spoken aloud.",
          outcomeLabel: "How She Expressed It",
        },
      ],
    },
  },
  {
    name: "Gentleness",
    icon: "🌿",
    accent: "#2a5c3a",
    struggling: {
      desc: "Struggling with anger, harshness, or pride",
      verse:
        '"A soft answer turns away wrath, but a harsh word stirs up anger." — Proverbs 15:1',
      prayerStarter:
        "God, my anger has gotten out of control and my words have caused damage I cannot undo. I am ashamed of my harshness toward _____. Help me be slow to speak and quick to listen. Give me the supernatural restraint that only Your Spirit can produce. Show me how to repair what my sharpness has broken — with gentleness, truth, and genuine humility...",
      people: [
        {
          icon: "😡",
          name: "Moses at Meribah",
          role: "Deliverer of Israel",
          context:
            "Numbers 12:3 calls Moses 'very humble, more than all men.' Yet at Meribah, after years of leading a complaining people, Moses struck the rock in anger instead of speaking to it as God commanded. One moment of harshness had lasting consequences.",
          quote:
            "'Hear now, you rebels! Must we bring water for you out of this rock?' Then Moses lifted his hand and struck the rock twice with his rod.",
          ref: "Numbers 20:10–11",
          outcome:
            "Moses' anger cost him the Promised Land — not because God was cruel, but because it misrepresented God's character to the people. Gentleness is power under control. When we are exhausted and overwhelmed, harshness creeps in first. Guard your speech most fiercely when you are most depleted.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🙌",
          name: "Jesus — Gentle and Lowly",
          role: "Son of God",
          context:
            "Jesus, who had authority over wind and sea, described Himself as 'gentle and lowly in heart.' He welcomed the children others pushed away, touched the lepers others avoided, and knelt in the dirt with those others condemned.",
          quote:
            "'Come to Me, all you who labor and are heavy laden, and I will give you rest... for I am gentle and lowly in heart, and you will find rest for your souls.'",
          ref: "Matthew 11:28–29",
          outcome:
            "Jesus knew when to turn over tables and when to kneel in the dust. Gentleness in Him was not the absence of conviction — it was the mastery of it. Ask God to show you which moment you are in. Sometimes gentleness is boldness; sometimes it is silence. Both require the Spirit.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Growing in gentleness and experiencing its fruit in relationships",
      verse:
        '"The servant of the Lord must not quarrel but be gentle to all, able to teach, patient." — 2 Timothy 2:24',
      prayerStarter:
        "Lord, thank You for teaching me gentleness — I know how hard my heart used to be. Thank You for the relationships that are healthier because of the work Your Spirit has done in me. Help me continue to cultivate a gentle spirit and use it to serve people who are still where I once was. Let my gentleness be a safe landing place for people who are hurting...",
      people: [
        {
          icon: "🤲",
          name: "Abigail",
          role: "Wife of Nabal, Later Wife of David",
          context:
            "When David came to kill her entire household because of her foolish husband's insults, Abigail rode out alone to meet him with food, wisdom, and a gentle word that stopped an army in its tracks.",
          quote:
            "When Abigail saw David, she dismounted quickly from the donkey, fell on her face before David, and bowed down to the ground.",
          ref: "1 Samuel 25:23",
          outcome:
            "Abigail's gentleness — humble, wise, and well-timed — saved many lives and earned her the lasting respect of a king. Her softness was not weakness; it was strategic wisdom. When gentleness is flowing from you, use it to de-escalate, to intercede, to stand between anger and its consequences.",
          outcomeLabel: "How She Expressed It",
        },
        {
          icon: "💌",
          name: "Paul Writing to Timothy",
          role: "Apostle and Mentor",
          context:
            "Paul wrote to the young, anxious Timothy with remarkable gentleness — patient, never crushing the bruised reed. He treated a fearful young pastor like a son, not a failure.",
          quote:
            "Let no one despise your youth, but be an example to the believers in word, in conduct, in love, in spirit, in faith, in purity.",
          ref: "1 Timothy 4:12",
          outcome:
            "Paul's gentleness produced a generation of faithful leaders. He multiplied his faith through patient, gentle investment in one person at a time. When gentleness marks your life, pour it into someone younger. Mentor with encouragement. Correct with kindness. You are shaping the next generation.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
  {
    name: "Self-Control",
    icon: "⚖️",
    accent: "#4a3a1a",
    struggling: {
      desc: "Struggling with destructive habits or impulses",
      verse:
        '"No temptation has overtaken you except what is common to man. But God is faithful, who will also make the way of escape, that you may be able to bear it." — 1 Corinthians 10:13',
      prayerStarter:
        "Lord, I am struggling with _____ and I feel like I keep losing the same battle. I am tired of failing and I need something that goes deeper than willpower. Come into this specific place of weakness. Help me find the way of escape You promise is always there. I don't want to white-knuckle this — I want to be genuinely changed from the inside out by Your Spirit...",
      people: [
        {
          icon: "💪",
          name: "Samson",
          role: "Judge of Israel",
          context:
            "Samson was given supernatural strength and a divine calling — and he repeatedly wasted it on Philistine women, revenge, and pride. He revealed his secret to Delilah despite knowing her intentions.",
          quote:
            "And she said, 'The Philistines are upon you, Samson!' So he awoke from his sleep... But he did not know that the LORD had departed from him.",
          ref: "Judges 16:20",
          outcome:
            "Samson's greatest enemy was himself. His gifts outlasted his discipline, until they didn't. Yet in his final moment, blind and humbled, he prayed — and God answered. It is never too late to cry out. But do not wait until you are blind and grinding grain. Start the fight for self-control today.",
          outcomeLabel: "How He Overcame",
        },
        {
          icon: "🏃",
          name: "Joseph and Potiphar's Wife",
          role: "Son of Jacob, Slave in Egypt",
          context:
            "Joseph was relentlessly pursued by Potiphar's wife over many days. He was young, alone, far from home, with every temptation and opportunity to sin without anyone knowing.",
          quote:
            "But he left his garment in her hand, and fled and ran outside.",
          ref: "Genesis 39:12",
          outcome:
            "Joseph's self-control was not passive — it was a sprint. He did not negotiate or linger; he ran. Sometimes self-control looks like a conversation — and sometimes it looks like fleeing. Know which moments require which response. God honored Joseph's run with faithfulness, and eventually a throne.",
          outcomeLabel: "How He Overcame",
        },
      ],
    },
    thriving: {
      desc: "Experiencing breakthrough and walking in Spirit-led discipline",
      verse:
        '"For God has not given us a spirit of fear, but of power and of love and of a sound mind." — 2 Timothy 1:7',
      prayerStarter:
        "Father, thank You for the breakthrough You have brought in this area of my life. I know the discipline I am walking in is not my own strength — it is the fruit of Your Spirit. Don't let me become proud or forget where I was. Help me use this freedom to help others who are still in the struggle I came out of. Let my testimony be their hope that change is possible...",
      people: [
        {
          icon: "📖",
          name: "Daniel and His Friends",
          role: "Exiles in Babylon",
          context:
            "As teenagers in Babylon, Daniel and his friends resolved not to defile themselves with the king's food. They asked for a test — ten days of vegetables and water — and trusted God with the outcome.",
          quote:
            "Daniel purposed in his heart that he would not defile himself with the portion of the king's delicacies... and God gave them knowledge and skill in all literature and wisdom.",
          ref: "Daniel 1:8,17",
          outcome:
            "Their self-control was purposeful and fruitful. God rewarded it with health, wisdom, and extraordinary favor. When discipline is producing fruit in your life, give God the credit publicly. Let others see that what you gave up has been replaced with something far better.",
          outcomeLabel: "How They Expressed It",
        },
        {
          icon: "🌊",
          name: "Paul — Buffeting His Body",
          role: "Apostle",
          context:
            "Paul described his own spiritual discipline as running with purpose and boxing with precision — not shadow-boxing. He spoke of bringing his body into subjection so that after preaching to others, he himself would not be disqualified.",
          quote:
            "I discipline my body and bring it into subjection, lest, when I have preached to others, I myself should become disqualified.",
          ref: "1 Corinthians 9:27",
          outcome:
            "Paul's discipline was not about earning favor — it was about finishing well and staying fit for the mission. When self-control marks your life, use it in service of something greater than yourself. Let your discipline be a gift to the people you are called to serve.",
          outcomeLabel: "How He Expressed It",
        },
      ],
    },
  },
];

// ─── SUB-COMPONENTS ───────────────────────────────────────────────────────────

const PersonCard = ({ person, isThriving }) => (
  <div className="fots-person-card">
    <div className="fots-person-header">
      <div className="fots-person-ornament">{person.icon}</div>
      <div>
        <div className="fots-person-name">{person.name}</div>
        <div className="fots-person-role">{person.role}</div>
      </div>
    </div>

    <div className={`fots-section-label ${isThriving ? "green" : ""}`}>
      {isThriving ? "Their Example" : "Their Struggle"}
    </div>
    <p className="fots-person-context">{person.context}</p>

    <div className={`fots-section-label ${isThriving ? "green" : ""}`}>
      What They Said / Prayed
    </div>
    <div className={`fots-quote-block ${isThriving ? "green" : ""}`}>
      <div className="fots-quote-text">"{person.quote}"</div>
      <div className="fots-quote-ref">— {person.ref}</div>
    </div>

    <div className={`fots-section-label ${isThriving ? "green" : ""}`}>
      {person.outcomeLabel}
    </div>
    <div className={`fots-outcome-block ${isThriving ? "green" : "red"}`}>
      <p className="fots-outcome-text">{person.outcome}</p>
    </div>
  </div>
);

const Modal = ({ fruit, mode, onClose }) => {
  const isThriving = mode === "thriving";
  const data = fruit[mode];

  // Close on overlay click
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fots-modal-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className={`fots-modal ${isThriving ? "thriving" : ""}`}>
        <div className="fots-modal-accent-bar" />

        <button
          className="fots-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ✕
        </button>

        <div className="fots-modal-header">
          <div
            className={`fots-modal-mode-label ${
              isThriving ? "thriving" : "struggling"
            }`}
          >
            {isThriving ? "🌿 I'm Thriving" : "🌧 I'm Struggling"}
          </div>

          <span className="fots-modal-icon">{fruit.icon}</span>
          <div className="fots-modal-title">{fruit.name}</div>
          <div className="fots-modal-subtitle">{data.desc}</div>
          <div className="fots-modal-verse">{data.verse}</div>
        </div>

        <div className="fots-modal-body">
          <div
            className={`fots-prayer-starter ${isThriving ? "thriving" : ""}`}
          >
            <p className="fots-prayer-text">{data.prayerStarter}</p>
          </div>

          {data.people.map((person, i) => (
            <PersonCard key={i} person={person} isThriving={isThriving} />
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const FruitOfTheSpirit = () => {
  const [mode, setMode] = useState("struggling");
  const [selectedFruit, setSelectedFruit] = useState(null);

  const { setShowLayout } = useLayout();

  useEffect(() => {
    setShowLayout(false);
  }, [setShowLayout]);

  // Close modal on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setSelectedFruit(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selectedFruit ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [selectedFruit]);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setSelectedFruit(null);
  };

  return (
    <div
      className={`fots-container ${mode === "thriving" ? "thriving-mode" : ""}`}
    >
      {/* ── HEADER ── */}
      <div className="fots-header">
        <div className="fots-header-ornament">✦ ✦ ✦</div>
        <p className="fots-header-verse">Galatians 5:22–23</p>
        <h1 className="fots-title">Fruit of the Spirit</h1>
        <h2 className="fots-subtitle">
          Biblical Encouragement for Every Season
        </h2>
        <div className="fots-header-divider" />
      </div>

      {/* ── MODE TOGGLE ── */}
      <div className="fots-toggle-wrap">
        <button
          className={`fots-toggle-btn struggling-btn ${
            mode === "struggling" ? "active" : ""
          }`}
          onClick={() => handleModeChange("struggling")}
        >
          <span className="fots-btn-icon">🌧️</span>
          <span className="fots-btn-label">I'm Struggling</span>
        </button>
        <button
          className={`fots-toggle-btn thriving-btn ${
            mode === "thriving" ? "active" : ""
          }`}
          onClick={() => handleModeChange("thriving")}
        >
          <span className="fots-btn-icon">🌿</span>
          <span className="fots-btn-label">I'm Thriving</span>
        </button>
      </div>

      <p className="fots-tagline">
        {mode === "struggling"
          ? "Choose a fruit you are struggling to bear — you are not alone."
          : "Choose a fruit to celebrate — and give thanks with those who have gone before."}
      </p>

      <p className="fots-intro">
        {mode === "struggling"
          ? "Scripture is full of real people who wrestled with the very same things you face today — and found grace. Choose the fruit you need help with."
          : "When God is moving in your life, join the great cloud of witnesses who praised Him in the same way. Choose the fruit you are grateful for today."}
      </p>

      {/* ── FRUIT GRID ── */}
      <div className="fots-grid">
        {fruits.map((fruit, i) => {
          const data = fruit[mode];
          return (
            <div
              key={fruit.name}
              className="fots-card"
              style={{
                "--card-accent": fruit.accent,
                animationDelay: `${i * 0.05}s`,
              }}
              onClick={() => setSelectedFruit(fruit)}
            >
              <span className="fots-card-icon">{fruit.icon}</span>
              <div className="fots-card-name">{fruit.name}</div>
              <div className="fots-card-desc">{data.desc}</div>
              <span className="fots-card-badge">
                {mode === "struggling"
                  ? "🌧 I'm struggling"
                  : "🌿 I'm thriving"}
              </span>
            </div>
          );
        })}
      </div>

      {/* ── FOOTER ── */}
      <div className="fots-footer">
        "His grace is sufficient for you, for His power is made perfect in
        weakness." — 2 Corinthians 12:9
      </div>

      {/* ── MODAL ── */}
      {selectedFruit && (
        <Modal
          fruit={selectedFruit}
          mode={mode}
          onClose={() => setSelectedFruit(null)}
        />
      )}
    </div>
  );
};

export default FruitOfTheSpirit;
